import { FC, useState } from 'react';
import {
  StreamQueryQuery,
  StreamState,
  StreamType,
  UnauthenticatedStreamQueryQuery,
} from '@/__generated__/graphql.ts';
import { StripeBillingInterval } from '@/__generated__/graphql.ts';
import {
  CustomCollection,
  Stream,
  StreamDeck,
} from '@/__generated__/schema-types.ts';
import { ListForm } from '@/components/stream-deck/list-form/list-form.tsx';
import { ManageListModal } from '@/components/stream-deck/manage-list-modal.tsx';
import { BannerActionsMenu } from '@/components/streams/stream-banner/banner-actions-menu.tsx';
import { BannerDescription } from '@/components/streams/stream-banner/banner-description.tsx';
import {
  BannerHeader,
  CreatedBy,
} from '@/components/streams/stream-banner/banner-header.tsx';
import { hasRightsToStream } from '@/components/streams/stream-banner/has-rights-to-stream.ts';
import { useAddCustomCollection } from '@/routes/_app.p.news-deck/use-add-custom-collection.ts';
import { useEditCustomCollection } from '@/routes/_app.p.news-deck/use-edit-custom-collection.ts';
import { CustomStreamInfoCard } from '@/routes/_app.p.streams_.$id/stream-banner/custom-stream-components/custom-stream-info-card.tsx';
import { CustomTargetsData } from '@/routes/_app.p.streams_.$id/stream-banner/custom-stream-components/custom-targets-data.tsx';
import { useStreamDecks } from '@/routes/_app/use-stream-decks.ts';
import { convertCentsToDollars } from '@/utils/convert-cents-to-dollars.ts';
import {
  getMonthlyPrice,
  getStartingMonthlyPrice,
  getSubscriptionTotalPrice,
} from '@/utils/paid-data.ts';
import {
  streamBannerBody,
  streamBannerBodyContainer,
  streamBannerContentContainer,
  streamName,
} from './stream-banner-content.css.ts';

export const StreamBannerContent: FC<{
  stream: StreamQueryQuery['stream'];
  isMe: boolean;
  activeSubscription?: boolean;
}> = ({ stream, isMe }) => {
  const hasRights = hasRightsToStream(stream!);
  const streamDecks = useStreamDecks() as StreamDeck[];
  const [isNewColumnOpen, setIsNewColumnOpen] = useState(false);
  const [selectedStreamDeck, setSelectedStreamDeck] =
    useState<StreamDeck | null>(null);
  const [selectedStreamDeckColumn, setSelectedStreamDeckColumn] = useState<
    CustomCollection | 'new' | null
  >(null);
  const { addCollection, loading } = useAddCustomCollection(true);

  const { editCustomCollection, loading: editLoading } =
    useEditCustomCollection(true);

  if (!stream) {
    return null;
  }

  const newStreamDeckSource = stream as unknown as Stream;

  let newSources = [newStreamDeckSource];

  if (selectedStreamDeckColumn && selectedStreamDeckColumn !== 'new') {
    const alreadyExists = selectedStreamDeckColumn.sources.find(
      (source) => source.id === newStreamDeckSource.id,
    );

    newSources = alreadyExists
      ? selectedStreamDeckColumn.sources
      : [...selectedStreamDeckColumn.sources, newStreamDeckSource];
  }

  const streamMonthlyPrice = getMonthlyPrice(stream.paidData);

  /**
   * Show price from `subscription` for subscribers,
   * show price from `paidData` for author (because he doesn't have subscription).
   */
  const streamPrice = stream.paidData?.length
    ? stream.subscription
      ? getSubscriptionTotalPrice(stream.subscription, stream.customTargetsData)
      : (getStartingMonthlyPrice(stream.paidData, stream.customTargetsData) ??
        null)
    : null;

  const priceInterval =
    stream.subscription?.interval ?? StripeBillingInterval.Month;
  const priceIntervalCount = stream.subscription?.intervalCount ?? 1;

  return (
    <div className={streamBannerContentContainer}>
      <BannerHeader
        author={stream.author}
        price={streamPrice ? convertCentsToDollars(streamPrice) : undefined}
        activeSubscription={hasRights}
        isAuthor={isMe}
        customStream={!!stream.customTargetsData}
        packageOnly={stream.packageOnly}
        packages={stream.inPackages}
        streamState={stream.streamState}
        streamType={stream.streamType}
        interval={priceInterval}
        intervalCount={priceIntervalCount}
        apiAccessEnabled={stream.apiAccessEnabled}
        createdByPrefix={
          stream.customTargetsData
            ? 'Custom paid stream'
            : stream.paidData?.length
              ? 'Paid stream'
              : stream.packageOnly
                ? 'In-package stream'
                : 'Free stream'
        }
        actionsMenu={
          <BannerActionsMenu
            streamId={stream.id}
            isAuthor={isMe}
            canAddToStreamDeck={stream.canAddToStreamDeck}
            isPaidStream={!!stream.paidData?.length}
            isCancelled={!!stream.subscription?.cancelAt}
            accessAllowed={hasRights}
            streamDecks={streamDecks}
            onStreamColumnSelect={(streamDeck, streamDeckColumn) => {
              setSelectedStreamDeck(streamDeck);
              setSelectedStreamDeckColumn(streamDeckColumn);
              setIsNewColumnOpen(true);
            }}
            isCustomStream={!!stream.customTargetsData}
            isPrivateStream={stream.streamType === StreamType.Private}
            isDraft={stream.streamState === StreamState.Draft}
          />
        }
      />

      <div className={streamBannerBodyContainer}>
        <div>
          <div className={streamBannerBody}>
            <h1 className={streamName}>{stream.name}</h1>
            <BannerDescription descriptionJson={stream.descriptionJson} />
          </div>

          {stream.customTargetsData && streamMonthlyPrice ? (
            <CustomTargetsData
              customTargetsData={stream.customTargetsData}
              price={convertCentsToDollars(streamMonthlyPrice)}
            />
          ) : null}
        </div>

        {stream.customTargetsData ? <CustomStreamInfoCard /> : null}
      </div>
      {selectedStreamDeckColumn && selectedStreamDeck && (
        <ManageListModal
          title={selectedStreamDeckColumn === 'new' ? 'Add list' : 'Edit list'}
          isOpen={isNewColumnOpen}
          onOpenChange={(open) => {
            setIsNewColumnOpen(open);
            if (!open) {
              setSelectedStreamDeck(null);
              setSelectedStreamDeckColumn(null);
            }
          }}
        >
          <ListForm
            isSubmitLoading={loading || editLoading}
            submitText={
              selectedStreamDeckColumn === 'new' ? 'Create' : 'Update'
            }
            initialValues={{
              name:
                selectedStreamDeckColumn === 'new'
                  ? ''
                  : selectedStreamDeckColumn.name,
              sources: newSources,
              highlights:
                selectedStreamDeckColumn === 'new'
                  ? undefined
                  : selectedStreamDeckColumn.highlights,
              filters:
                selectedStreamDeckColumn === 'new'
                  ? {}
                  : selectedStreamDeckColumn.filters,
            }}
            submit={async (val) => {
              if (selectedStreamDeckColumn === 'new') {
                await addCollection({
                  ...val,
                  streamDeckId: selectedStreamDeck.id,
                });
              } else {
                await editCustomCollection({
                  ...val,
                  id: selectedStreamDeckColumn.id,
                  streamDeckId: selectedStreamDeck.id,
                });
              }
              setIsNewColumnOpen(false);
              setSelectedStreamDeck(null);
              setSelectedStreamDeckColumn(null);
            }}
          />
        </ManageListModal>
      )}
    </div>
  );
};

export const UnauthorizedStreamBannerContent: FC<{
  stream: UnauthenticatedStreamQueryQuery['stream'];
}> = ({ stream }) => {
  return stream ? (
    <div className={streamBannerContentContainer}>
      <CreatedBy author={stream.author} prefix={'Stream'} />
      <div className={streamBannerBody}>
        <h1 className={streamName}>{stream.name}</h1>
        <BannerDescription
          descriptionJson={stream.descriptionJson}
          withShowMore={false}
        />
      </div>
    </div>
  ) : null;
};
